import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Divider, Drawer, Form, Input, List, Popover, Typography } from 'antd';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationResult } from 'react-query';
import styled from 'styled-components';

import { mapFormValuesToPayload } from '../common/filterHelpers';
import { Filter } from '../common/filters.type';
import { SearchFormProps } from '../common/searchFormField.types';
import { useViewport } from '../common/useViewPort';
import Icon, { ButtonIcon, iconColors } from '../icons/Icon';

import { StyledButton } from './Button';
import { StyledColumn } from './StyledColumn';
import { StyledRow } from './StyledRow';

const { Text } = Typography;

const Header: React.FC = () => (
  <StyledRow gap="0.7rem" justifyContent="start">
    <span style={{ display: 'inline-flex', alignItems: 'center' }}></span>
    Filters
  </StyledRow>
);

const PlusIcon = styled(ButtonIcon).attrs({
  name: 'Plus',
})``;

const StyledFiltersListItem = styled(List.Item)`
  padding-left: 0px !important;
  padding-right: 0px !important;
  cursor: pointer;
  height: 2.3rem;
  width: 100%;
  justify-content: space-evenly;

  :hover {
    background-color: #f5f5f5;
  }
`;

type FiltersListProps = {
  filters: Filter[];
  currentFilter?: Filter;
  handleClick?: (value: string) => void;
  resetFilterToDefault: () => void;
  updateFilter?: () => void;
  deleteFilter: UseMutationResult<
    AxiosResponse<unknown>,
    AxiosError<unknown, unknown>,
    { queryParams?: object; data?: unknown },
    unknown
  >;
};

const FiltersList: React.FC<FiltersListProps> = ({
  filters,
  currentFilter,
  handleClick,
  resetFilterToDefault,
  updateFilter,
  deleteFilter,
}) => {
  const data = filters.map((filter) => filter.name);

  const handleDeleteFilter = (item: string) => {
    const filterToDelete = filters.find((filter) => filter.name === item)?.id;
    if (!filterToDelete) return;
    deleteFilter.mutate({ queryParams: { id: filterToDelete.toString() } });
    resetFilterToDefault();
  };

  const isFilterCurrent = (filter: string) => {
    return filter === currentFilter?.name;
  };

  const ListItem: React.FC<{ item: string; isChosen: boolean }> = ({ item, isChosen }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <StyledFiltersListItem onMouseOver={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <StyledRow justifyContent="space-between">
          <div style={{ flex: '1' }} onClick={() => handleClick?.(item)}>
            {item}
          </div>
          {isChosen || hovered ? (
            <div>
              <Button type="text" size="small" onClick={updateFilter}>
                <Icon name="Save" inlineStyle style={{ color: iconColors.black }} />
              </Button>
              <Button type="text" size="small" onClick={() => handleDeleteFilter(item)}>
                <Icon name="Delete" />
              </Button>
            </div>
          ) : (
            <></>
          )}
        </StyledRow>
      </StyledFiltersListItem>
    );
  };

  return (
    <List
      header={<Text strong>Saved filters</Text>}
      dataSource={data}
      renderItem={(item: string) => <ListItem item={item} isChosen={isFilterCurrent(item)} />}
      split={false}
      style={{ width: '30%' }}
    />
  );
};

type SavePopoverProps = {
  onSave: (name: string) => void;
  onCancel: () => void;
};

const SavePopover: React.FC<SavePopoverProps> = ({ onCancel, onSave }) => {
  const [name, setName] = useState('');
  const onNameProvided = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value);

  return (
    <StyledColumn gap="0.5rem" style={{ width: '18rem' }}>
      <Typography.Text>Enter new filter name</Typography.Text>
      <Input onChange={onNameProvided}></Input>
      <StyledRow justifyContent="end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" disabled={!name.length} onClick={() => onSave(name)}>
          Save
        </Button>
      </StyledRow>
    </StyledColumn>
  );
};

type FooterProps = {
  onSubmit: () => void;
  onSave: (name: string) => void;
  onCancel: () => void;
};

const Footer: React.FC<FooterProps> = ({ onSubmit, onSave, onCancel }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = (name: string) => {
    onSave(name);
    setIsOpen(false);
  };

  return (
    <StyledRow justifyContent="start" style={{ marginLeft: '10px' }}>
      <StyledButton
        type="primary"
        icon={<ButtonIcon name="Filter" style={{ color: `${iconColors.white}` }} />}
        onClick={onSubmit}
      >
        Apply
      </StyledButton>
      <Popover
        title="Save filter"
        open={isOpen}
        content={<SavePopover onCancel={() => setIsOpen(false)} onSave={handleSave} />}
        trigger="click"
      >
        <StyledButton
          onClick={() => setIsOpen(true)}
          icon={
            <div style={{ display: 'inline-block', alignContent: 'center' }}>
              <PlusIcon />
            </div>
          }
        >
          Save
        </StyledButton>
      </Popover>
      <StyledButton onClick={onCancel}>Cancel</StyledButton>
    </StyledRow>
  );
};

type SearchDrawerProps = {
  open: boolean;
  filters?: Filter[];
  currentFilter: Filter;
  searchForm: FunctionComponent<SearchFormProps>;
  handleSubmit: (values: Filter) => void;
  close: () => void;
  resetFilterToDefault: () => void;
  onSave: UseMutationResult<
    AxiosResponse<unknown>,
    AxiosError<unknown, unknown>,
    { id?: string; data?: unknown },
    unknown
  >;
  onUpdate: UseMutationResult<
    AxiosResponse<unknown>,
    AxiosError<unknown, unknown>,
    { queryParams?: object; data?: unknown },
    unknown
  >;
  onDelete: UseMutationResult<
    AxiosResponse<unknown>,
    AxiosError<unknown, unknown>,
    { queryParams?: object; data?: unknown },
    unknown
  >;
};

const FilterContainer = styled(StyledColumn)`
  width: 75%;
  padding-left: 8px;
  padding-top: 12px;
  padding-right: 6px;
  overflow-y: scroll;
  overflow-x: hidden;

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
  }

  .ant-form::-webkit-scrollbar {
    width: 6px;
  }
  .ant-form::-webkit-scrollbar-track {
    background: #f5f5f5;
  }

  .ant-form::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
  }
`;

export const SearchDrawer: React.FC<SearchDrawerProps> = ({
  open,
  filters,
  currentFilter,
  handleSubmit,
  close,
  resetFilterToDefault,
  onSave,
  onUpdate,
  onDelete,
  searchForm: SearchForm,
}) => {
  const [form] = Form.useForm();
  const [currentChosenFilter, setCurrentChosenFilter] = useState<Filter>(currentFilter);
  const { isMediumDesktop } = useViewport();

  useEffect(() => {
    setCurrentChosenFilter(currentFilter);
  }, [currentFilter]);

  if (!filters) return <></>;

  const chooseFilter = (name: string) => {
    form.resetFields();
    const filterToSet = filters.find((filter) => filter.name === name);
    if (filterToSet) {
      setCurrentChosenFilter(filterToSet);
    }
  };

  const handleSubmitForm = () => {
    const formValuesToPayload = mapFormValuesToPayload(form.getFieldsValue());
    const modifiedCurrentFilter = {
      ...currentChosenFilter,
      criteria: formValuesToPayload,
    };
    handleSubmit(modifiedCurrentFilter);
    close();
  };

  const handleSaveFilter = (filterName: string) => {
    const formValuesToPayload = mapFormValuesToPayload(form.getFieldsValue());
    const modifiedCurrentFilter = {
      name: filterName,
      sort: currentFilter.sort,
      criteria: formValuesToPayload,
    };
    onSave.mutate({ data: modifiedCurrentFilter });
  };

  const handleUpdateFilter = () => {
    const formValuesToPayload = mapFormValuesToPayload(form.getFieldsValue());
    const { id, ...modifiedCurrentFilter } = {
      ...currentChosenFilter,
      criteria: formValuesToPayload,
    };
    onUpdate?.mutate({ data: modifiedCurrentFilter, queryParams: { id: id.toString() } });
  };

  return (
    <Drawer
      title={<Header />}
      bodyStyle={{ paddingTop: '0px' }}
      headerStyle={{ border: 'none', paddingBottom: '0px' }}
      footer={<Footer onSubmit={handleSubmitForm} onSave={handleSaveFilter} onCancel={close} />}
      footerStyle={{ border: 'none', paddingBottom: '2rem' }}
      open={open}
      width={860}
      onClose={close}
      closable={false}
      keyboard
      rootStyle={isMediumDesktop ? { height: '70vh', top: '15vh' } : { height: '50vh', top: '25vh' }}
      maskStyle={isMediumDesktop ? { height: '100vh', top: '-15vh' } : { height: '100vh', top: '-25vh' }}
    >
      <StyledRow gap="1px" justifyContent="space-between" height="100%">
        <FiltersList
          filters={filters}
          currentFilter={currentChosenFilter}
          handleClick={chooseFilter}
          resetFilterToDefault={resetFilterToDefault}
          updateFilter={handleUpdateFilter}
          deleteFilter={onDelete}
        />
        <div>
          <Divider type="vertical" style={{ height: '100%' }} />
        </div>
        <FilterContainer>
          <SearchForm form={form} filter={currentChosenFilter} />
        </FilterContainer>
      </StyledRow>
    </Drawer>
  );
};
